export function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/** Valida dígitos verificadores do CNPJ (14 dígitos, só números). */
export function validateCnpj(cnpj: string): boolean {
  const cleaned = onlyDigits(cnpj)
  if (cleaned.length !== 14) return false
  if (/^(\d)\1{13}$/.test(cleaned)) return false

  const digits = cleaned.split('').map(Number)

  let sum = 0
  let weight = 5
  for (let i = 0; i < 12; i++) {
    sum += digits[i] * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  let remainder = sum % 11
  const digit1 = remainder < 2 ? 0 : 11 - remainder
  if (digits[12] !== digit1) return false

  sum = 0
  weight = 6
  for (let i = 0; i < 13; i++) {
    sum += digits[i] * weight
    weight = weight === 2 ? 9 : weight - 1
  }
  remainder = sum % 11
  const digit2 = remainder < 2 ? 0 : 11 - remainder
  return digits[13] === digit2
}

/** Máscara progressiva enquanto digita */
export function maskCpf(value: string): string {
  const digits = onlyDigits(value).slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

export function maskCnpj(value: string): string {
  const digits = onlyDigits(value).slice(0, 14)
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2')
}

export function maskCep(value: string): string {
  const digits = onlyDigits(value).slice(0, 8)
  return digits.replace(/(\d{5})(\d)/, '$1-$2')
}

export function maskPhone(value: string): string {
  const digits = onlyDigits(value).slice(0, 11)
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{4})(\d)/, '$1-$2')
  }
  return digits
    .replace(/(\d{2})(\d)/, '($1) $2')
    .replace(/(\d{5})(\d)/, '$1-$2')
}

export function formatCpf(value?: string | null): string {
  const digits = onlyDigits(value ?? '')
  if (digits.length !== 11) return value ?? ''
  return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
}

export function formatCnpj(value?: string | null): string {
  const digits = onlyDigits(value ?? '')
  if (digits.length !== 14) return value ?? ''
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

export function formatCep(value?: string | null): string {
  const digits = onlyDigits(value ?? '')
  if (digits.length !== 8) return value ?? ''
  return digits.replace(/(\d{5})(\d{3})/, '$1-$2')
}

export function formatPhone(value?: string | null): string {
  const digits = onlyDigits(value ?? '')
  if (digits.length === 11) {
    return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }
  if (digits.length === 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  }
  return value ?? ''
}

export function formatEndereco(pj: {
  logradouro?: string | null
  numero?: string | null
  complemento?: string | null
  bairro?: string | null
  cidade?: string | null
  uf?: string | null
  cep?: string | null
}): string {
  const parts = [
    [pj.logradouro, pj.numero].filter(Boolean).join(', '),
    pj.complemento,
    pj.bairro,
    [pj.cidade, pj.uf].filter(Boolean).join(' - '),
    pj.cep ? formatCep(pj.cep) : null,
  ].filter(Boolean)
  return parts.join(' · ') || '—'
}

export function formatContaBanco(pj: {
  bancoNome?: string | null
  bancoAgencia?: string | null
  bancoConta?: string | null
  bancoTipoConta?: string | null
  pixChave?: string | null
}): string {
  const bank = [pj.bancoNome, pj.bancoAgencia ? `Ag ${pj.bancoAgencia}` : null, pj.bancoConta ? `Cc ${pj.bancoConta}` : null]
    .filter(Boolean)
    .join(' · ')
  if (bank && pj.pixChave) return `${bank} · PIX: ${pj.pixChave}`
  if (bank) return bank
  if (pj.pixChave) return `PIX: ${pj.pixChave}`
  return '—'
}

export function formatInviteTimeRemaining(expiresAt: string, isExpired?: boolean): string {
  if (isExpired) return 'Expirado'

  const diffMs = new Date(expiresAt).getTime() - Date.now()
  if (diffMs <= 0) return 'Expirado'

  const totalMinutes = Math.floor(diffMs / 60_000)
  if (totalMinutes < 60) {
    return `Expira em ${totalMinutes} min`
  }

  const totalHours = Math.floor(totalMinutes / 60)
  if (totalHours < 48) {
    const minutes = totalMinutes % 60
    return minutes > 0 ? `Expira em ${totalHours}h ${minutes}min` : `Expira em ${totalHours}h`
  }

  const days = Math.floor(totalHours / 24)
  const hours = totalHours % 24
  return hours > 0 ? `Expira em ${days}d ${hours}h` : `Expira em ${days}d`
}
